import { useIntl } from "react-intl";
import { fromStoragePercent, toStoragePercent } from "@/lib/skill-levels";
import { newCondition, type Composition, type ConditionByTypename, type MatchCondition } from "../design-codec";
import type { MatchData } from "../match-design-types";
import { SubsectionHeader } from "./row-primitives";
import {
  MatchSkillsDiagram,
  type MatchSkillEntry,
  type MatchSkillRequirement,
} from "./MatchSkillsDiagram";

// Fachkompetenzen als 2D-Raster (Stufe x Muss/Kann) ueber die match-eigene MatchSkillsDiagram.
// Geteilt zwischen Rank (1 Person, personCount unset -> Verhalten unveraendert) und
// Team/ASSESS+OPTIMIZE (personCount=true: fixiert Operator/Composition auf GE/MIN,
// siehe normalize(), und zeigt die Personenzahl-Eingabe). Analog zu DetailsSection.singlePerson.
type ExpertiseCondition = ConditionByTypename<"ExpertiseCondition">;

export function ExpertiseSection({
  data,
  update,
  readOnly = false,
  personCount = false,
  className = "",
}: {
  data: MatchData;
  update: (patch: Partial<MatchData>) => void;
  readOnly?: boolean;
  // Team-Kontext: erzwingt "mindestens Stufe X"/"mindestens N Personen" statt
  // Operator-/Composition-Wahl, siehe normalize(); Rank laesst es unset.
  personCount?: boolean;
  className?: string;
}) {
  const intl = useIntl();

  const expertise = data.conditions.filter(
    (c): c is ExpertiseCondition => c.__typename === "ExpertiseCondition",
  );

  // Team-Vereinfachung: keine Operator-/Composition-Wahl mehr, immer "mindestens
  // Stufe X" / "mindestens N Personen". Legacy-Zeilen mit anderem Operator/
  // Composition (LE/EQ, MAX/EXACT/ALL/ANY) werden beim reinen Anzeigen NICHT
  // umgeschrieben (kein Write aus einer readOnly-Ansicht heraus) - erst eine
  // tatsaechliche Interaktion normalisiert, analog AiModeOnRead/
  // CriterionRequirementOnRead in design-codec.ts (coerce on read, kein
  // erzwungener Write).
  function normalize(cond: ExpertiseCondition): ExpertiseCondition {
    if (!personCount) return cond;
    return {
      ...cond,
      operator: "GE",
      composition: { operator: "MIN", value: cond.composition.value ?? 1 },
    };
  }

  const entries: MatchSkillEntry[] = expertise.map((c, i) => ({
    id: `req-exp-${i}`,
    description: c.expertise,
    level: fromStoragePercent(Number(c.level)),
    requirement: c.requirement,
    personCount: personCount ? c.composition.value ?? 1 : undefined,
  }));

  function writeExpertise(next: ExpertiseCondition[]) {
    const others = data.conditions.filter((c) => c.__typename !== "ExpertiseCondition");
    update({ conditions: [...others, ...next] as MatchCondition[] });
  }
  const indexOf = (id: string) => Number(id.replace("req-exp-", ""));
  function replaceAt(i: number, cond: ExpertiseCondition) {
    const next = expertise.slice();
    next[i] = normalize(cond);
    writeExpertise(next);
  }

  function onPlace(id: string, level: number) {
    const i = indexOf(id);
    const cur = expertise[i];
    if (!cur) return;
    replaceAt(i, { ...cur, level: level === 0 ? 0 : toStoragePercent(level) });
  }
  function onToggleRequirement(id: string, requirement: MatchSkillRequirement) {
    const i = indexOf(id);
    const cur = expertise[i];
    if (!cur) return;
    replaceAt(i, { ...cur, requirement });
  }
  function onAdd(
    description: string,
    level: number,
    requirement: MatchSkillRequirement,
    initialPersonCount?: number,
  ) {
    // Team: composition direkt korrekt anlegen (kein Altdaten-Fall bei Neuanlage,
    // normalize() ist hier nicht noetig). Rank: undefined durchreichen, newCondition
    // greift dann auf seinen eigenen Default zurueck ({operator: "ALL", value: null}).
    const composition: Composition | undefined = personCount
      ? { operator: "MIN", value: initialPersonCount ?? 1 }
      : undefined;
    // newCondition setzt requirement auf den Default OPTIONAL; der Add-Editor kann Muss mitgeben.
    const created = newCondition("ExpertiseCondition", {
      expertise: description,
      level: level === 0 ? 0 : toStoragePercent(level),
      aiMode: "NO_AI",
      operator: "GE",
      composition,
    });
    writeExpertise([...expertise, { ...created, requirement }]);
  }
  function onRename(id: string, description: string) {
    const i = indexOf(id);
    const cur = expertise[i];
    if (!cur) return;
    replaceAt(i, { ...cur, expertise: description });
  }
  function onDelete(id: string) {
    const i = indexOf(id);
    if (!expertise[i]) return;
    const next = expertise.slice();
    next.splice(i, 1);
    writeExpertise(next);
  }
  function onPersonCountChange(id: string, count: number) {
    const i = indexOf(id);
    const cur = expertise[i];
    if (!cur) return;
    replaceAt(i, { ...cur, composition: { operator: "MIN", value: count } });
  }

  return (
    <div className={className}>
      <SubsectionHeader
        title={intl.formatMessage({ id: "match.recruiting.expertiseTitle", defaultMessage: "Benötigte Fachkompetenzen (mind.)" })}
      />
      <MatchSkillsDiagram
        entries={entries}
        readOnly={readOnly}
        personCount={personCount}
        onPlace={onPlace}
        onToggleRequirement={onToggleRequirement}
        onAdd={onAdd}
        onRename={onRename}
        onDelete={onDelete}
        onPersonCountChange={personCount ? onPersonCountChange : undefined}
      />
    </div>
  );
}
